/**
 * Whether a JWT's `exp` has passed, read without verifying the signature.
 *
 * This is only ever used to decide whether it is worth *presenting* a token —
 * the API verifies it properly and is the only thing that trusts it. An
 * unparseable token counts as expired, so a malformed cookie degrades to the
 * signed-out path instead of throwing.
 *
 * Lives here rather than in `proxy.ts` so the proxy and the server components
 * that forward the same cookie share one definition.
 */
export function isTokenExpired(token: string): boolean {
  // A missing or malformed cookie is routine traffic (bots, stale cookies), so
  // it counts as expired without being reported — the proxy runs this on every
  // page request.
  const payload = token.split('.')[1];
  if (!payload) return true;

  try {
    // JWT segments are base64url; `atob` only accepts the standard alphabet.
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );

    const expiry = decoded?.exp ?? 0;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
  } catch {
    return true;
  }
}
